import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Factory, HeartPulse, ShoppingBag, GraduationCap, Building2, Briefcase,
  Plane, Users, Truck, Store, Rocket,
} from "lucide-react";
import SectionHead from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";

const INDUSTRIES = [
  { icon: Factory, name: "Manufacturing & Industrial", desc: "Product catalogues, dealer enquiry capture, and AI agents that answer specification and pricing questions around the clock." },
  { icon: HeartPulse, name: "Hospitals & Clinics", desc: "Appointment booking websites, doctor profiles, and AI voice agents that handle patient calls and callback requests." },
  { icon: ShoppingBag, name: "Retail & Wholesale", desc: "Catalogue websites with WhatsApp ordering conversations, stock enquiry automation, and repeat-customer follow-ups." },
  { icon: GraduationCap, name: "Educational Institutions", desc: "Admission enquiry websites, course information chatbots, and AI agents that qualify and route parent enquiries." },
  { icon: Building2, name: "Real Estate", desc: "Property listing websites, site-visit booking automation, and instant responses to buyer and tenant enquiries." },
  { icon: Briefcase, name: "Consultants & Professional Services", desc: "Credibility-building websites with consultation booking and AI-qualified lead capture for every practice area." },
  { icon: Plane, name: "Travel & Hospitality", desc: "Booking-focused websites, room and package enquiries, and AI agents that respond to travellers day and night." },
  { icon: Users, name: "Recruitment & Job Consultancies", desc: "Candidate and employer enquiry funnels, vacancy listings, and automated first-round screening conversations." },
  { icon: Truck, name: "Logistics & Distribution", desc: "Service area websites, quote request automation, and AI agents that capture shipment enquiries instantly." },
  { icon: Store, name: "Small & Medium Enterprises", desc: "Affordable professional websites with enquiry capture that grows with your business — upgrade to AI automation when ready." },
  { icon: Rocket, name: "Startups & Growing Businesses", desc: "Launch-ready landing pages, demo booking flows, and scalable automation that keeps pace with your growth." },
];

export default function Industries() {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "Industries We Serve | RIDGELINE DIGI PARTNER";
  }, []);

  return (
    <div className="pt-[84px]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <SectionHead eyebrow="INDUSTRIES WE SERVE" title="Technology Solutions for Every Growing Business."
            description="Every industry wins and serves customers differently. We shape websites and AI automation around your industry's real customer journey — from first enquiry to closed deal." />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} delay={Math.min(i * 0.06, 0.5)}>
                <div className="group glass-card rounded-2xl p-7 h-full transition-all duration-300 hover:-translate-y-1.5 hover:border-blue-500/40">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600/15 border border-blue-500/30 text-blue-400 transition-colors group-hover:bg-blue-600/25">
                    <ind.icon size={20} />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-slate-50">{ind.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{ind.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <p className="text-sm text-slate-400 mb-5">Don&apos;t see your industry? We build for any business that values its customers.</p>
            <button onClick={() => navigate("/contact")}
              className="rounded-full bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-[0_0_28px_rgba(37,99,235,0.4)] transition-all hover:bg-blue-500 hover:-translate-y-0.5">
              Discuss Your Industry&apos;s Needs
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
